/* eslint-disable react/no-array-index-key */
import React, {
  useState, useEffect, useCallback, Fragment,
} from 'react';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import moment from 'moment';
import {
  FiChevronRight, FiChevronLeft, FiChevronDown, FiChevronUp,
} from 'react-icons/fi';

import {
  CalendarWrapper,
  CalendarTitle,
  CalendarContent,
  CalendarCell,
  CalendarTitleActions,
  CalendarTitleContent,
  CalendarTitleIcon,
  CalendarEventDotWrapper,
  CalendarEventDot,
} from './styles';

const Calendar = ({
  date, onDaySelect, onChangeMonth, activeDays, isOpen,
}) => {
  const weekdayshort = moment.weekdaysShort();
  const [currentDate, setCurrentDate] = useState(moment());
  const [openCalendar, setOpenCalendar] = useState(isOpen);
  const [selectedDay, setSelectedDay] = useState(moment().format('D'));

  useEffect(() => {
    const isValidDate = moment(date).isValid();
    setCurrentDate(isValidDate ? moment(date) : moment);
  }, [date]);

  const firstDayOfMonth = () => {
    const firstDay = currentDate.startOf('month').format('d');
    return firstDay;
  };

  const handleChangeMonth = useCallback(
    (monthDiff) => {
      const currentMonth = parseInt(moment(currentDate).format('M'), 10);
      const newDate = moment(currentDate).set('month', currentMonth + monthDiff - 1);
      onChangeMonth(newDate);
      setCurrentDate(newDate);
    },
    [currentDate, onChangeMonth],
  );

  const handleDayClick = useCallback(
    (day) => {
      const selectedDate = moment(currentDate).set('date', day);
      onDaySelect(selectedDate.format('MM/DD/YYYY'));
      setSelectedDay(day);
      setOpenCalendar(false);
    },
    [onDaySelect, currentDate],
  );

  const blanks = [];
  for (let i = 0; i < firstDayOfMonth(); i += 1) {
    blanks.push(<CalendarCell />);
  }

  const daysInMonth = currentDate.daysInMonth();
  const days = [];
  for (let i = 1; i <= daysInMonth; i += 1) {
    const isAvailableDay = activeDays.find(({ day }) => i === day);
    const isActiveDay = i.toString() === selectedDay.toString();
    const isCurrentMonth = moment().format('M Y') === currentDate.format('M Y');
    const today = moment().format('D');
    const isToday = isCurrentMonth && i.toString() === today;

    days.push(
      <CalendarCell
        today={isToday}
        active={!!isAvailableDay && isActiveDay}
        available={!!isAvailableDay}
        onClick={() => {
          if (isAvailableDay) {
            handleDayClick(isAvailableDay.day);
          }
        }}
      >
        {i}
        {isToday && (
          <CalendarEventDotWrapper>
            <CalendarEventDot bgColor="var(--brand-color)" />
          </CalendarEventDotWrapper>
        )}
      </CalendarCell>,
    );
  }

  return (
    <CalendarWrapper>
      <CalendarTitle>
        <CalendarTitleIcon>{openCalendar ? <FiChevronUp /> : <FiChevronDown />}</CalendarTitleIcon>
        <CalendarTitleContent onClick={() => setOpenCalendar(!openCalendar)}>
          {currentDate.format('MMMM, YYYY')}
        </CalendarTitleContent>
        <CalendarTitleActions>
          <IconButton className="icon_end" aria-label="Edit" onClick={() => handleChangeMonth(-1)}>
            <FiChevronLeft />
          </IconButton>
          <IconButton className="icon_end" aria-label="Edit" onClick={() => handleChangeMonth(1)}>
            <FiChevronRight />
          </IconButton>
        </CalendarTitleActions>
      </CalendarTitle>
      <CalendarContent open={openCalendar}>
        {weekdayshort.map((wd) => (
          <CalendarCell key={wd}>{wd[0]}</CalendarCell>
        ))}
        {blanks.map((b, i) => (
          <Fragment key={`blank_${i}`}>{b}</Fragment>
        ))}
        {days.map((d, i) => (
          <Fragment key={`day_${i}`}>{d}</Fragment>
        ))}
      </CalendarContent>
    </CalendarWrapper>
  );
};

Calendar.defaultProps = {
  date: '',
  activeDays: [],
  onDaySelect: () => true,
  onChangeMonth: () => true,
  isOpen: false,
};

Calendar.propTypes = {
  date: PropTypes.string,
  onDaySelect: PropTypes.func,
  onChangeMonth: PropTypes.func,
  activeDays: PropTypes.arrayOf(PropTypes.shape()),
  isOpen: PropTypes.bool,
};

export default Calendar;
